import React, { useState, useEffect, useCallback, useContext } from "react";
import { Button, Input } from "reactstrap";
import * as XLSX from "xlsx";
import Header from "../../layout/Header/Header";
import { posGet, avisaError, dinero, numero, hoy, sumaDias, METODOS } from "./posApi";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import { AuthContext } from "../../contexts/AuthContext";
import SinPermiso from "./SinPermiso";
import FotoModelo from "../Articulos/FotoModelo";

const AZUL = "#2563eb";   // periodo actual / serie principal
const NARANJA = "#d97706"; // periodo de comparación
const MUTED = "#64748b";

const inicioMes = (f) => f.slice(0, 8) + "01";
const PRESETS = [
  { k: "hoy", label: "Hoy", rango: () => [hoy(), hoy()] },
  { k: "7", label: "7 días", rango: () => [sumaDias(hoy(), -6), hoy()] },
  { k: "mes", label: "Este mes", rango: () => [inicioMes(hoy()), hoy()] },
  { k: "30", label: "30 días", rango: () => [sumaDias(hoy(), -29), hoy()] },
];

// Periodo anterior del mismo largo, o el mismo periodo del año pasado
const periodoAnterior = (i, f, modo) => {
  if (modo === "anio") return [String(Number(i.slice(0, 4)) - 1) + i.slice(4), String(Number(f.slice(0, 4)) - 1) + f.slice(4)];
  const dias = Math.round((new Date(f) - new Date(i)) / 864e5) + 1;
  return [sumaDias(i, -dias), sumaDias(i, -1)];
};

const Kpi = ({ icon, color, label, value, sub }) => (
  <div className="col-6 col-lg-3 mb-3">
    <div className="kpi-card">
      <div className="kpi-icon" style={{ background: `${color}14`, color }}><i className={icon} /></div>
      <div>
        <div className="kpi-label">{label}</div>
        <div className="kpi-value">{value}</div>
        {sub && <div style={{ fontSize: "0.72rem", color: MUTED }}>{sub}</div>}
      </div>
    </div>
  </div>
);

// Barras horizontales de una sola serie (magnitud); la etiqueta del valor va en tinta de texto
const Barras = ({ filas, etiqueta, valor, formato = dinero, max = 15 }) => {
  const top = filas.slice(0, max);
  const tope = Math.max(...top.map(valor), 1);
  return (
    <div>
      {top.map((f, i) => (
        <div key={i} title={`${etiqueta(f)}: ${formato(valor(f))}`} style={{ display: "grid", gridTemplateColumns: "minmax(70px, 30%) minmax(40px, 1fr) auto", gap: 8, alignItems: "center", padding: "3px 0" }}>
          <div style={{ fontSize: "0.78rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{etiqueta(f)}</div>
          <div style={{ background: "#f1f5f9", borderRadius: 4, height: 14 }}>
            <div style={{ width: `${Math.max((valor(f) / tope) * 100, 0.5)}%`, background: AZUL, height: 14, borderRadius: "0 4px 4px 0" }} />
          </div>
          <div style={{ fontSize: "0.78rem", fontWeight: 700, minWidth: 70, textAlign: "right", whiteSpace: "nowrap" }}>{formato(valor(f))}</div>
        </div>
      ))}
      {filas.length > max && <div className="small text-muted mt-1">Y {filas.length - max} más en la tabla.</div>}
    </div>
  );
};

// Línea de tiempo con 1 o 2 series, crosshair y tooltip
const Lineas = ({ puntos, series, alto = 220 }) => {
  const [hover, setHover] = useState(null);
  const W = 760, H = alto, M = { l: 56, r: 12, t: 12, b: 26 };
  if (!puntos.length) return <div className="text-muted small">Sin datos en el periodo</div>;
  const maxY = Math.max(...puntos.flatMap((p) => series.map((s) => p[s.k] || 0)), 1);
  const x = (i) => M.l + (puntos.length === 1 ? (W - M.l - M.r) / 2 : (i * (W - M.l - M.r)) / (puntos.length - 1));
  const y = (v) => H - M.b - (v / maxY) * (H - M.t - M.b);
  const ticks = [0, 0.5, 1].map((t) => t * maxY);
  const paso = Math.ceil(puntos.length / 8);
  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }} onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={M.l} x2={W - M.r} y1={y(t)} y2={y(t)} stroke="#e2e8f0" strokeWidth="1" />
            <text x={M.l - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill={MUTED}>{t >= 1000 ? `$${(t / 1000).toLocaleString("es-MX", { maximumFractionDigits: 1 })}k` : `$${Math.round(t)}`}</text>
          </g>
        ))}
        {puntos.map((p, i) => (i % paso === 0 ? <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill={MUTED}>{p.etiqueta}</text> : null))}
        {series.map((s) => (
          <polyline key={s.k} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" points={puntos.map((p, i) => `${x(i)},${y(p[s.k] || 0)}`).join(" ")} />
        ))}
        {puntos.length <= 31 && series.map((s) => puntos.map((p, i) => (
          <circle key={`${s.k}-${i}`} cx={x(i)} cy={y(p[s.k] || 0)} r="4" fill={s.color} stroke="#fff" strokeWidth="2" />
        )))}
        {hover !== null && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={M.t} y2={H - M.b} stroke="#94a3b8" strokeWidth="1" />
            {series.map((s) => <circle key={s.k} cx={x(hover)} cy={y(puntos[hover][s.k] || 0)} r="4" fill={s.color} stroke="#fff" strokeWidth="2" />)}
          </g>
        )}
        {puntos.map((p, i) => (
          <rect key={i} x={x(i) - (W - M.l - M.r) / Math.max(puntos.length, 1) / 2} y={M.t} width={(W - M.l - M.r) / Math.max(puntos.length, 1)} height={H - M.t - M.b} fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
      </svg>
      {hover !== null && (
        <div style={{ position: "absolute", top: 8, left: `${Math.min((x(hover) / W) * 100, 70)}%`, background: "#0f172a", color: "#f8fafc", borderRadius: 8, padding: "6px 10px", fontSize: "0.75rem", pointerEvents: "none" }}>
          {series.map((s) => (
            <div key={s.k}><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: s.color, marginRight: 6 }} />{puntos[hover][s.fecha || "etiqueta"] || puntos[hover].etiqueta}: <strong>{dinero(puntos[hover][s.k])}</strong></div>
          ))}
        </div>
      )}
      {series.length > 1 && (
        <div style={{ display: "flex", gap: 16, fontSize: "0.75rem", color: MUTED, justifyContent: "center" }}>
          {series.map((s) => <span key={s.k}><span style={{ display: "inline-block", width: 14, height: 3, background: s.color, verticalAlign: "middle", marginRight: 6 }} />{s.label}</span>)}
        </div>
      )}
    </div>
  );
};

const excel = (nombre, filas) => {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas), "Reporte");
  XLSX.writeFile(wb, `${nombre}.xlsx`);
};

function PosReportes() {
  const { user } = useContext(AuthContext);
  const verCostos = !!(user && user.posCostos);
  const ctx = useTienda(true);
  const { tienda } = ctx;
  // Se abre en la pestaña que eligieron en el menú de Reportes (?tab=resumen|comparativo|top)
  const [tab, setTab] = useState(() => { const t = new URLSearchParams(window.location.search).get("tab"); return ["resumen", "comparativo", "top"].includes(t) ? t : "resumen"; });
  const [inicio, setInicio] = useState(inicioMes(hoy()));
  const [fin, setFin] = useState(hoy());
  const [modoComp, setModoComp] = useState("anterior");
  const [bInicio, setBInicio] = useState("");
  const [bFin, setBFin] = useState("");
  const [por, setPor] = useState("articulo");
  const [resumen, setResumen] = useState(null);
  const [comp, setComp] = useState(null);
  const [top, setTop] = useState([]);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (modoComp === "manual") return;
    const [a, b] = periodoAnterior(inicio, fin, modoComp);
    setBInicio(a); setBFin(b);
  }, [inicio, fin, modoComp]);

  const cargar = useCallback(async () => {
    if (!tienda || !inicio || !fin) return;
    setCargando(true);
    try {
      if (tab === "resumen") setResumen(await posGet("/reportes/resumen", { area: tienda, inicio, fin }));
      if (tab === "comparativo" && bInicio && bFin) setComp(await posGet("/reportes/comparativo", { area: tienda, aInicio: inicio, aFin: fin, bInicio, bFin }));
      if (tab === "top") setTop(await posGet("/reportes/top", { area: tienda, inicio, fin, por }));
    } catch (e) { avisaError(e); } finally { setCargando(false); }
  }, [tab, tienda, inicio, fin, bInicio, bFin, por]);
  useEffect(() => { cargar(); }, [cargar]);

  const t = resumen && resumen.totales;
  const variacion = (v) => (v === null || v === undefined ? "—" : `${v > 0 ? "▲" : v < 0 ? "▼" : ""} ${Math.abs(v).toFixed(1)}%`);
  const colorVar = (v) => (v > 0 ? "#059669" : v < 0 ? "#dc2626" : MUTED);

  if (!(user && user.rolTiendas && user.posReportes)) return <SinPermiso que="ver reportes" />;

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-chart-line mr-2" style={{ color: AZUL }} />Reportes de tiendas</h3>
            <p className="page-subtitle">Venta neta = ventas − devoluciones (sin canceladas){verCostos ? " · utilidad = venta − costo de lo vendido" : ""}</p>
          </div>
          <div className="page-actions">
            <Button color="secondary" size="sm" href="/PosReportes"><i className="fas fa-th-large mr-1" />Reportes</Button>
          </div>
        </div>

        <div className="btn-group mb-3" role="group">
          {[["resumen", "Resumen", "fas fa-tachometer-alt"], ["comparativo", "Comparativo", "fas fa-balance-scale"], ["top", "Lo más vendido", "fas fa-trophy"]].map(([k, l, ic]) => (
            <Button key={k} color={tab === k ? "primary" : "secondary"} outline={tab !== k} onClick={() => setTab(k)}><i className={`${ic} mr-1`} />{l}</Button>
          ))}
        </div>

        <div className="filter-bar">
          <TiendaSelector ctx={ctx} permiteTodas />
          <div><label>{tab === "comparativo" ? "Periodo A desde" : "Desde"}</label><Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></div>
          <div><label>Hasta</label><Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></div>
          <div style={{ flexDirection: "row", gap: 4 }}>
            {PRESETS.map((p) => <Button key={p.k} size="sm" color="light" style={{ border: "1px solid #e2e8f0" }} onClick={() => { const [a, b] = p.rango(); setInicio(a); setFin(b); }}>{p.label}</Button>)}
          </div>
          {tab === "comparativo" && (
            <>
              <div><label>Comparar contra</label>
                <Input type="select" value={modoComp} onChange={(e) => setModoComp(e.target.value)}>
                  <option value="anterior">Periodo anterior</option>
                  <option value="anio">Mismo periodo año pasado</option>
                  <option value="manual">Fechas a mano</option>
                </Input>
              </div>
              <div><label>Periodo B desde</label><Input type="date" value={bInicio} disabled={modoComp !== "manual"} onChange={(e) => setBInicio(e.target.value)} /></div>
              <div><label>Hasta</label><Input type="date" value={bFin} disabled={modoComp !== "manual"} onChange={(e) => setBFin(e.target.value)} /></div>
            </>
          )}
          {tab === "top" && (
            <div><label>Agrupar por</label>
              <Input type="select" value={por} onChange={(e) => setPor(e.target.value)}>
                <option value="articulo">Modelo</option><option value="linea">Línea</option><option value="marca">Marca</option>
                <option value="coleccion">Colección</option><option value="talla">Talla</option><option value="color">Color</option><option value="vendedor">Vendedor</option>
              </Input>
            </div>
          )}
          <div>{cargando && <i className="fas fa-spinner fa-spin" style={{ color: AZUL }} />}</div>
        </div>

        {tab === "resumen" && t && (
          <>
            <div className="row">
              <Kpi icon="fas fa-dollar-sign" color="#10b981" label="Venta" value={dinero(t.venta)} sub={verCostos ? `Utilidad ${dinero(t.utilidad)}` : "Neta de devoluciones"} />
              <Kpi icon="fas fa-receipt" color={AZUL} label="Tickets" value={numero(t.tickets)} sub={`Promedio ${dinero(t.ticketPromedio)}`} />
              <Kpi icon="fas fa-tshirt" color="#8b5cf6" label="Piezas" value={numero(t.piezas)} sub={t.tickets ? `${(t.piezas / t.tickets).toFixed(1)} por ticket` : ""} />
              <Kpi icon="fas fa-tags" color="#f59e0b" label="Promociones" value={dinero(t.descuento)} sub={[t.devoluciones ? `Devoluciones ${dinero(t.devoluciones)}` : "Descuento otorgado", t.gastosCaja ? `Gastos de caja ${dinero(t.gastosCaja)}` : ""].filter(Boolean).join(" · ")} />
            </div>
            <div className="row">
              <div className="col-lg-8 mb-3">
                <div className="form-section"><div className="form-section-header"><i className="fas fa-chart-line" /><h4 className="form-section-title">Venta por día</h4></div>
                  <div className="form-section-body"><Lineas puntos={resumen.porDia.map((d) => ({ etiqueta: d.fecha.slice(5), venta: d.venta }))} series={[{ k: "venta", color: AZUL, label: "Venta" }]} /></div>
                </div>
              </div>
              <div className="col-lg-4 mb-3">
                <div className="form-section"><div className="form-section-header"><i className="fas fa-wallet" /><h4 className="form-section-title">Formas de pago</h4></div>
                  <div className="form-section-body">
                    <Barras filas={resumen.porMetodo} etiqueta={(m) => METODOS[m.metodo]} valor={(m) => m.importe} />
                    <table className="table table-sm mt-2 mb-0">
                      <tbody>{resumen.porMetodo.map((m) => <tr key={m.metodo} style={{ whiteSpace: "nowrap" }}><td>{METODOS[m.metodo]}</td><td className="text-right">{numero(m.operaciones)} op.</td><td className="text-right">{t.venta ? ((m.importe / t.venta) * 100).toFixed(1) : 0}%</td></tr>)}</tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            {tienda === "todas" && (
              <div className="form-section"><div className="form-section-header"><i className="fas fa-store" /><h4 className="form-section-title">Ranking de tiendas</h4>
                <Button size="sm" color="warning" className="ml-auto" onClick={() => excel("ranking_tiendas", resumen.porTienda.map((f, i) => ({ Lugar: i + 1, Tienda: f.tienda, Venta: f.venta, Tickets: f.tickets, Piezas: f.piezas, TicketPromedio: f.ticketPromedio, Descuento: f.descuento, ...(verCostos ? { Utilidad: f.utilidad } : {}), GastosCaja: f.gastosCaja })))}><i className="fas fa-file-excel mr-1" />Excel</Button></div>
                <div className="form-section-body">
                  <Barras filas={resumen.porTienda} etiqueta={(f) => f.tienda} valor={(f) => f.venta} max={20} />
                  <div className="table-responsive mt-3">
                    <table className="table table-sm">
                      <thead><tr><th>#</th><th>Tienda</th><th className="text-right">Venta</th><th className="text-right">Tickets</th><th className="text-right">Ticket prom.</th><th className="text-right">Piezas</th><th className="text-right">Descuento</th>{verCostos && <th className="text-right">Utilidad</th>}<th className="text-right">Gastos de caja</th><th className="text-right">% del total</th></tr></thead>
                      <tbody>{resumen.porTienda.map((f, i) => (
                        <tr key={f.area}><td>{i + 1}</td><td>{f.tienda}</td><td className="text-right"><strong>{dinero(f.venta)}</strong></td><td className="text-right">{numero(f.tickets)}</td><td className="text-right">{dinero(f.ticketPromedio)}</td><td className="text-right">{numero(f.piezas)}</td><td className="text-right">{dinero(f.descuento)}</td>{verCostos && <td className="text-right">{dinero(f.utilidad)}</td>}<td className="text-right">{dinero(f.gastosCaja)}</td><td className="text-right">{t.venta ? ((f.venta / t.venta) * 100).toFixed(1) : 0}%</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {tab === "comparativo" && comp && (
          <>
            <div className="row">
              <Kpi icon="fas fa-calendar-check" color={AZUL} label={`A: ${inicio} al ${fin}`} value={dinero(comp.totalA)} />
              <Kpi icon="fas fa-calendar" color={NARANJA} label={`B: ${bInicio} al ${bFin}`} value={dinero(comp.totalB)} />
              <Kpi icon="fas fa-percentage" color={comp.totalA >= comp.totalB ? "#10b981" : "#ef4444"} label="Variación" value={variacion(comp.totalB ? ((comp.totalA - comp.totalB) / comp.totalB) * 100 : null)} sub={dinero(comp.totalA - comp.totalB)} />
            </div>
            <div className="form-section"><div className="form-section-header"><i className="fas fa-chart-line" /><h4 className="form-section-title">Día por día{tienda !== "todas" ? ` · ${ctx.nombreTienda}` : " · todas las tiendas"}</h4></div>
              <div className="form-section-body">
                <Lineas
                  puntos={comp.serie.map((s) => ({ etiqueta: `D${s.dia}`, a: s.ventaA, b: s.ventaB, fa: s.fechaA, fb: s.fechaB }))}
                  series={[{ k: "a", color: AZUL, label: `Periodo A (${inicio} – ${fin})`, fecha: "fa" }, { k: "b", color: NARANJA, label: `Periodo B (${bInicio} – ${bFin})`, fecha: "fb" }]}
                />
              </div>
            </div>
            <div className="form-section"><div className="form-section-header"><i className="fas fa-store" /><h4 className="form-section-title">Por tienda</h4>
              <Button size="sm" color="warning" className="ml-auto" onClick={() => excel("comparativo_tiendas", comp.filas.map((f) => ({ Tienda: f.tienda, VentaA: f.a.venta, VentaB: f.b.venta, VariacionPct: f.variacion, TicketsA: f.a.tickets, TicketsB: f.b.tickets, TicketPromA: f.a.ticketPromedio, TicketPromB: f.b.ticketPromedio })))}><i className="fas fa-file-excel mr-1" />Excel</Button></div>
              <div className="form-section-body table-responsive">
                <table className="table table-sm">
                  <thead><tr><th>Tienda</th><th className="text-right">Venta A</th><th className="text-right">Venta B</th><th className="text-right">Variación</th><th className="text-right">Tickets A / B</th><th className="text-right">Ticket prom. A / B</th><th className="text-right">Piezas A / B</th></tr></thead>
                  <tbody>
                    {!comp.filas.length && <tr><td colSpan={7} className="text-center text-muted">Sin ventas en ninguno de los dos periodos</td></tr>}
                    {comp.filas.map((f) => (
                      <tr key={f.area}>
                        <td>{f.tienda}</td>
                        <td className="text-right"><strong>{dinero(f.a.venta)}</strong></td>
                        <td className="text-right">{dinero(f.b.venta)}</td>
                        <td className="text-right" style={{ color: colorVar(f.variacion), fontWeight: 700 }}>{variacion(f.variacion)}</td>
                        <td className="text-right">{numero(f.a.tickets)} / {numero(f.b.tickets)}</td>
                        <td className="text-right">{dinero(f.a.ticketPromedio)} / {dinero(f.b.ticketPromedio)}</td>
                        <td className="text-right">{numero(f.a.piezas)} / {numero(f.b.piezas)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === "top" && (
          <div className="form-section"><div className="form-section-header"><i className="fas fa-trophy" /><h4 className="form-section-title">Lo más vendido por {({ articulo: "modelo", linea: "línea", marca: "marca", coleccion: "colección", talla: "talla", color: "color", vendedor: "vendedor" })[por]}</h4>
            <Button size="sm" color="warning" className="ml-auto" disabled={!top.length} onClick={() => excel(`top_${por}`, top.map((f) => ({ Nombre: f.nombre, Venta: f.venta, Piezas: f.piezas, Tickets: f.tickets, Descuento: f.descuento, ...(verCostos ? { Utilidad: f.utilidad } : {}) })))}><i className="fas fa-file-excel mr-1" />Excel</Button></div>
            <div className="form-section-body">
              {!top.length ? <div className="text-muted">Sin ventas en el periodo</div> : (
                <>
                  <Barras filas={top} etiqueta={(f) => f.nombre || "Sin asignar"} valor={(f) => f.venta} />
                  <div className="table-responsive mt-3">
                    <table className="table table-sm">
                      <thead><tr><th>#</th><th>Nombre</th><th className="text-right">Venta</th><th className="text-right">Piezas</th><th className="text-right">Tickets</th><th className="text-right">Descuento</th>{verCostos && <><th className="text-right">Utilidad</th><th className="text-right">Margen</th></>}</tr></thead>
                      <tbody>{top.map((f, i) => (
                        <tr key={i}><td>{i + 1}</td><td>{por === "articulo" ? <span style={{ display: "flex", alignItems: "center", gap: 10 }}><FotoModelo url={f.urlFoto} nombre={f.nombre} size={36} />{f.nombre || "Sin asignar"}</span> : (f.nombre || "Sin asignar")}</td><td className="text-right"><strong>{dinero(f.venta)}</strong></td><td className="text-right">{numero(f.piezas)}</td><td className="text-right">{numero(f.tickets)}</td><td className="text-right">{dinero(f.descuento)}</td>{verCostos && <><td className="text-right">{dinero(f.utilidad)}</td><td className="text-right">{f.venta ? ((f.utilidad / f.venta) * 100).toFixed(1) : 0}%</td></>}</tr>
                      ))}</tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default PosReportes;
